/**
 * Plot Matrix (JSON) dev-tool — API snapshot extraction.
 *
 * Pulls a raw `GroundworksData`-shaped object straight off a `boq-versions`
 * API response (either `useBoqLatestVersion`'s or `useBoqSelectedVersion`'s
 * `data`), rather than off `state.groundworks` (the live, possibly-unsaved
 * TenderContext value every other Plot Matrix consumer reads). This is the
 * data source for the dev-mode-only "Plot Matrix (JSON)" tab in
 * `GroundworksReview.tsx` — see `plotMatrixJsonBuilder.ts` for the next step.
 *
 * The response shape varies by caller (`getBoqVersion` wraps the saved
 * document under `boq`; other paths return it under `data`/`snapshot`/bare) —
 * the same four-branch fallback already used throughout `GroundworksReview.tsx`
 * (e.g. `apiDynamicBillSummaryTabs`) is repeated here for both the raw
 * take-off tables (top-level `groundworks`) and the computed
 * `billOfQuantities[0].groundworks.summary` extra-items/Dynamic Bill Summary
 * data layered on top.
 */
import {
  deserializeDynamicBillSummaryTabs,
  deserializeCutFillsExtraItems,
  deserializeDrainageExtraItems,
} from './dynamicBillSummarySerialization';

export function extractApiGroundworksSnapshot(
  apiVersionData: unknown,
): Record<string, unknown> | undefined {
  const d = apiVersionData as any;
  if (!d) return undefined;

  const rawGroundworks: Record<string, unknown> | undefined =
    d?.boq?.groundworks ||
    d?.groundworks ||
    d?.snapshot?.groundworks ||
    d?.data?.groundworks;

  if (!rawGroundworks) return undefined;

  const summaryForExtras =
    d?.boq?.billOfQuantities?.[0]?.groundworks?.summary ||
    d?.billOfQuantities?.[0]?.groundworks?.summary ||
    d?.snapshot?.billOfQuantities?.[0]?.groundworks?.summary ||
    d?.data?.billOfQuantities?.[0]?.groundworks?.summary;

  const dynamicBillSummaryTabs = deserializeDynamicBillSummaryTabs(summaryForExtras);

  return {
    ...rawGroundworks,
    ...(dynamicBillSummaryTabs.length > 0 ? { dynamicBillSummaryTabs } : {}),
    cutFillsExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.cutFills),
    foundationsExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.foundations),
    drainageExtraItems: deserializeDrainageExtraItems(summaryForExtras?.drainage),
    plotDrivesExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.plotDrives),
    plotServiceExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.plotService),
    scaffoldExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.scaffold),
    plotPavingsExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.plotPavings),
    gardenExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.garden),
    retainingWallsExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.retainingWalls),
    geoWallsExtraItems: deserializeCutFillsExtraItems(summaryForExtras?.geoWalls),
  };
}
